import React, { useState, useRef } from 'react';
import { Card, Form, Button, Modal } from 'react-bootstrap';
import { slugify } from '../../utils/slugify';
import { confirmAlert } from 'react-confirm-alert';

// Keep `position` in sync with the visual (array) order so the value we send to
// the backend is always contiguous and 0-based.
const reindex = (mods) => mods.map((mod, idx) => ({ ...mod, position: idx }));

export default function ModulesList({ modules, setModules }) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [tempConfig, setTempConfig] = useState('');
  // Held in a ref (not state) so starting a drag doesn't re-render and abort it.
  const dragItem = useRef(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  // Raw text held while a position field is being edited (committed on blur/Enter).
  const [posDrafts, setPosDrafts] = useState({});

  const handleAddModule = () => {
    setModules(reindex([...modules, {
      name: '',
      slug: '',
      active: true,
      configuration: '{}'
    }]));
  };

  const handleDeleteModule = (index) => {
    confirmAlert({
      title: 'Confirm Delete',
      message: 'Are you sure you want to delete this module?',
      buttons: [
        {
          label: 'Yes',
          onClick: async () => {
            const newModules = [...modules];
            newModules.splice(index, 1);
            setModules(reindex(newModules));
          }
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  const handleModuleChange = (index, field, value) => {
    const newModules = [...modules];
    const module = newModules[index];

    if (field === 'name') {
      // Only update slug if it hasn't been manually edited
      if (module.slug === slugify(module.name)) {
        newModules[index] = {
          ...module,
          name: value,
          slug: slugify(value)
        };
      } else {
        newModules[index] = {
          ...module,
          name: value
        };
      }
    } else {
      newModules[index] = {
        ...module,
        [field]: field === 'active' ? value === '1' : value
      };
    }

    setModules(newModules);
  };

  // Move a module from one index to another and re-sync positions.
  const moveModule = (from, to) => {
    if (from === to || from == null || to == null) return;
    const bounded = Math.max(0, Math.min(to, modules.length - 1));
    if (from === bounded) return;
    const newModules = [...modules];
    const [moved] = newModules.splice(from, 1);
    newModules.splice(bounded, 0, moved);
    setModules(reindex(newModules));
  };

  // --- Manual position input (1-based for the user) ---
  const commitPosition = (index) => {
    const draft = posDrafts[index];
    setPosDrafts((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    if (draft === undefined || draft === '') return;
    const parsed = parseInt(draft, 10);
    if (Number.isNaN(parsed)) return;
    moveModule(index, parsed - 1);
  };

  // --- Drag and drop (native HTML5) ---
  const handleDragStart = (e, index) => {
    dragItem.current = index;
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox requires data to be set for the drag to actually start.
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overIndex !== index) setOverIndex(index);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    moveModule(dragItem.current, index);
    dragItem.current = null;
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleDragEnd = () => {
    dragItem.current = null;
    setDragIndex(null);
    setOverIndex(null);
  };

  return (
    <>
      <div className='row g-0'>
        {modules.map((module, index) => (
          <div
            key={index}
            className=' px-2 col-12 col-md-4'
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={(e) => handleDrop(e, index)}
          >
            <Card
              className='mb-3'
              style={{
                border: overIndex === index && dragIndex !== null ? '2px dashed #883484' : undefined,
                opacity: dragIndex === index ? 0.5 : 1
              }}
            >
            <Card.Header>
              <div className='d-flex justify-content-between align-items-center'>
                <div className='d-flex align-items-center gap-2'>
                  <span
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                    title='Drag to reorder'
                    style={{ cursor: 'grab', padding: '2px 6px' }}
                    className='text-muted'
                  >
                    <i className='fas fa-grip-vertical'></i>
                    <span style={{ marginLeft: '4px', letterSpacing: '-2px' }}>⋮⋮</span>
                  </span>
                  {module.id ? (
                    <span className='text-muted'>ID: {module.id}</span>
                  ) : (
                    <span className='text-muted'>New Module</span>
                  )}
                </div>
                <div className='d-flex align-items-center gap-1'>
                  <Form.Label className='mb-0 text-muted' style={{ fontSize: '0.85rem' }}>Position</Form.Label>
                  <Form.Control
                    type='number'
                    min={1}
                    max={modules.length}
                    size='sm'
                    style={{ width: '70px' }}
                    value={posDrafts[index] !== undefined ? posDrafts[index] : index + 1}
                    onChange={(e) => setPosDrafts((prev) => ({ ...prev, [index]: e.target.value }))}
                    onBlur={() => commitPosition(index)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
                  />
                </div>
              </div>
            </Card.Header>
              <Card.Body>
                <div className='row'>
                  <div className='col-md-6'>
                    <Form.Group className='mb-3'>
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        type='text'
                        value={module.name}
                        onChange={(e) => handleModuleChange(index, 'name', e.target.value)}
                        placeholder='Enter module name'
                      />
                    </Form.Group>
                  </div>
                  <div className='col-md-6'>
                    <Form.Group className='mb-3'>
                      <Form.Label>Slug</Form.Label>
                      <Form.Control
                        type='text'
                        value={module.slug}
                        onChange={(e) => handleModuleChange(index, 'slug', e.target.value)}
                        placeholder='Enter module slug'
                      />
                    </Form.Group>
                  </div>
                </div>
                <div className='row'>
                  <div className='col-md-6'>
                    <Form.Group className='mb-3'>
                      <Form.Label>Active</Form.Label>
                      <Form.Control
                        as='select'
                        value={module.active ? '1' : '0'}
                        onChange={(e) => handleModuleChange(index, 'active', e.target.value)}
                      >
                        <option value='1'>Yes</option>
                        <option value='0'>No</option>
                      </Form.Control>
                    </Form.Group>
                  </div>
                  <div className='col-md-12'>
                    <Form.Group className='mb-3'>
                      <Form.Label>Configuration</Form.Label>
                      <Form.Control
                        as='textarea'
                        rows={5}
                        value={typeof module.configuration === 'string' ? module.configuration : JSON.stringify(module.configuration, null, 2)}
                        onChange={(e) => handleModuleChange(index, 'configuration', e.target.value)}
                        placeholder='Enter configuration JSON'
                        // style={{ resize: 'both', minWidth: '100%' }}
                      />
                    </Form.Group>
                  </div>
                </div>
              </Card.Body>
              <Card.Footer>
                <div className='d-flex justify-content-between'>
                  <Button variant='outline-secondary' size='sm' onClick={() => {
                    setEditingIndex(index);
                    setTempConfig(
                      typeof module.configuration === 'string'
                        ? module.configuration
                        : JSON.stringify(module.configuration, null, 2)
                    );
                  }}>
                    Edit Configuration
                  </Button>
                  <Button
                    variant='danger'
                    size='sm'
                    onClick={() => handleDeleteModule(index)}
                  >
                    Remove
                  </Button>
                </div>
              </Card.Footer>
            </Card>
          </div>
        ))}
        <div className='px-2 col-12 col-md-4'>
          <Card
            className='mb-3'
            style={{ cursor: 'pointer', minHeight: '478px' }}
            onClick={handleAddModule}
          >
            <Card.Body className='d-flex align-items-center justify-content-center'>
              <div className='text-center text-muted'>
                <i className='fas fa-plus-circle fa-2x mb-2'></i>
                <h1>+</h1>
                <h5>Add New Module</h5>
              </div>
            </Card.Body>
          </Card>
        </div>
        <Modal show={editingIndex !== null} onHide={() => setEditingIndex(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Module Configuration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            as='textarea'
            rows={15}
            value={tempConfig}
            onChange={(e) => setTempConfig(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingIndex(null)}>Cancel</Button>
          <Button
            variant="success"
            onClick={() => {
              handleModuleChange(editingIndex, 'configuration', tempConfig);
              setEditingIndex(null);
            }}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
      </div>
    </>
  );
}
